import { useState } from "react";
import type { CustomDateProps } from "ag-grid-react";

import { Input } from "@/lib/shadcn/ui/input";

/**
 * Greek (`dd/mm/yyyy`) date selection component for AG Grid `agDateColumnFilter`
 * columns.
 *
 * AG Grid v35's native date filter uses the browser's `<input type="date">`,
 * which renders in the BROWSER locale (en-US -> `mm/dd/yyyy`). The Keros client
 * is in Greece and cannot change their browser locale, so the filter inputs read
 * back-to-front from the `dd/mm/yyyy` date *cells* (which `dateFormatter` already
 * renders via `toLocaleDateString("en-GB")`).
 *
 * AG Grid v35 does NOT let you reformat the native picker. The supported fix is a
 * custom Date Component (the colDef `dateComponent`). The React flavour is a
 * function component receiving {@link CustomDateProps} (`date` + `onDateChange`);
 * AG Grid drives `getDate`/`setDate` through those props, so we only render a
 * `dd/mm/yyyy` text input and translate it with {@link parseGreekDate} /
 * {@link formatGreekDate}. The TZ-independent `utcDateFilterComparator` still owns
 * matching — this component only changes the input UI, never the comparison.
 */

const GREEK_DATE_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** Format a Date as zero-padded day-first `dd/mm/yyyy`; null -> "". */
// eslint-disable-next-line react-refresh/only-export-components
export function formatGreekDate(date: Date | null): string {
    if (!date) return "";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = String(date.getFullYear()).padStart(4, "0");
    return `${dd}/${mm}/${yyyy}`;
}

/**
 * Parse a strict day-first `dd/mm/yyyy` string to a LOCAL-midnight Date, or null.
 *
 * Strict: the shape must be exactly `dd/mm/yyyy` (zero-padded), and the parsed
 * day/month/year must round-trip (so `30/02/2026` and `32/06/2026` are rejected
 * rather than silently rolling over). Local midnight matches what AG Grid hands
 * the comparator (`filterLocalDateAtMidnight`).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function parseGreekDate(value: string): Date | null {
    const match = GREEK_DATE_RE.exec(value.trim());
    if (!match) return null;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const date = new Date(year, month - 1, day, 0, 0, 0, 0);
    // Reject rolled-over calendar dates (e.g. Feb 30 -> Mar 2).
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }
    return date;
}

export function GreekDateComponent({ date, onDateChange, location }: CustomDateProps) {
    // Local input text is the source of truth while the user types; the incoming
    // `date` prop seeds it and overrides it whenever AG Grid sets a new value
    // (e.g. clearing the filter), tracked by `lastDate`.
    const [text, setText] = useState<string>(formatGreekDate(date));
    const [lastDate, setLastDate] = useState<Date | null>(date ?? null);

    if ((date ? date.getTime() : null) !== (lastDate ? lastDate.getTime() : null)) {
        setLastDate(date ?? null);
        setText(formatGreekDate(date));
    }

    const ariaLabel = location === "floatingFilter" ? "Filter date (dd/mm/yyyy)" : "Date (dd/mm/yyyy)";

    return (
        <Input
            type="text"
            inputMode="numeric"
            className="ag-input-field-input ag-text-field-input"
            placeholder="dd/mm/yyyy"
            aria-label={ariaLabel}
            value={text}
            onChange={(e) => {
                const next = e.target.value;
                setText(next);
                onDateChange(parseGreekDate(next));
            }}
        />
    );
}

export default GreekDateComponent;
